
import Component from 'vue-class-component';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import {PermissionsGroup} from '@/store/models.def';
import echarts from 'echarts';
import gdbx from '@/store/modules/gdbx';
import {DataViewFormatter} from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';
import moment from 'moment/moment';
import {GobiColor} from '@/helpers/color';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
    components: {
        IncludeDebitNoteToggle,
        IncludeCreditNoteToggle,
        SlPhModuleOnlyToggle,
        SecondaryLineBarChartFilterable,
    },
})
export default class SalesTotalSalesComparisonByRegionBarChart extends FilterWidget {
    public get permissionIds(): PermissionsGroup[] {
        return ['sales', 'customers'];
    }

    public dataZoom = [
        {
            type: 'slider',
        },
        {
            type: 'inside',
        },
    ];

    public filterIds: Array<
        | 'dateAsOf'
        | 'dateRange'
        | 'stockItems'
        | 'agents'
        | 'customers'
        | 'suppliers'
        | 'compareDate1'
        | 'compareDate2'
        | 'comparisonPeriod'
    > = [
        'customers',
        'agents',
        'compareDate1',
        'compareDate2',
        'comparisonPeriod',
    ];

    public xAxisData: string[] = [];
    public series: echarts.ECharts[] | any = [];

    public get currencySymbol() {
        return gdbx.currencySymbol;
    }

    public get yAxisLabel2() {
        let label: string = '';

        label = this.currencySymbol;

        return label;
    }

    public get dataViewFormatter() {
        return DataViewFormatter.salesTracker;
    }

    public get includeCreditNote() {
        return settingx.now.includeCreditNote;
    }

    public get includeDebitNote() {
        return settingx.now.includeDebitNote;
    }

    public get slPhModuleOnly() {
        return settingx.now.slPhModuleOnly;
    }

    public get dataDateRange() {
        return [
            moment(this.selectedDateRange[0]).subtract(1, 'year').valueOf(),
            this.selectedDateRange[1],
        ];
    }

    public get expensiveHook() {
        const {
            selectedDateRange,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        } = this;
        return JSON.stringify([
            selectedDateRange,
            includeCreditNote,
            includeDebitNote,
            slPhModuleOnly,
        ]);
    }

    public async expensiveCalc() {
        const jobId = this.currentJobId;
        this.generateLoadingText(0.2, 0.35);

        const queryHead = await this._ensureTablesLoaded(
            ['globalArea', 'sales', 'creditNotes', 'customerTrades'] as any[],
        );
        this.checkInterrupted(jobId);

        const areaTable = getTableName('globalArea');
        const salesTable = getTableName('sales');
        const cnTable = getTableName('creditNotes');
        const arTable = getTableName('customerTrades');

        const areaRows = await queryHead.rawQuery(`
            SELECT "CODE" as code, "DESCRIPTION" as descr FROM ${areaTable}
        `);
        const areaMap = new Map<string, string>();
        for (const r of areaRows) { areaMap.set(String(r.code), String(r.descr)); }
        this.checkInterrupted(jobId);
        this.generateLoadingText(0.35, 0.9);

        const pastYearDateRange: [number, number] = [
            moment(this.selectedDateRange[0]).subtract(1, 'year').valueOf(),
            moment(this.selectedDateRange[1]).subtract(1, 'year').valueOf(),
        ];

        const tyDateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
        const pyDateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${pastYearDateRange[0]} AND ${pastYearDateRange[1]}`;

        const thisYearByArea: Array<{ code: string; text: string; sum: number }> = [];
        const pastYearByArea: Array<{ code: string; text: string; sum: number }> = [];

        if (this.slPhModuleOnly) {
            const docTypes = this.includeDebitNote ? ['IV', 'CS', 'DN'] : ['IV', 'CS'];
            const docTypeFilter = ` AND ${COL.DOCTYPE} IN (${docTypes.map((t) => `'${t}'`).join(', ')})`;

            const areaQueryForTable = (table: string, filter: string) => `
                SELECT "AREA" as area, SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as totalAmt
                FROM ${table}
                WHERE ${filter} GROUP BY "AREA"
            `;
            const [tySales, pySales, tyCN, pyCN] = await Promise.all([
                queryHead.rawQuery(areaQueryForTable(salesTable, `${tyDateFilter}${docTypeFilter}`)),
                queryHead.rawQuery(areaQueryForTable(salesTable, `${pyDateFilter}${docTypeFilter}`)),
                queryHead.rawQuery(areaQueryForTable(cnTable, tyDateFilter)),
                queryHead.rawQuery(areaQueryForTable(cnTable, pyDateFilter)),
            ]);

            const toMap = (rows: any[]) => {
                const m = new Map<string, number>();
                for (const r of rows) { m.set(String(r.area), Number(r.totalAmt) || 0); }
                return m;
            };
            const tySalesMap = toMap(tySales);
            const pySalesMap = toMap(pySales);
            const tyCNMap = toMap(tyCN);
            const pyCNMap = toMap(pyCN);

            const allAreas = new Set([...tySalesMap.keys(), ...pySalesMap.keys()]);
            for (const area of allAreas) {
                if (!area) { continue; }
                const text = areaMap.get(area) || area;
                const tyS = tySalesMap.get(area) || 0;
                const tyC = this.includeCreditNote ? (tyCNMap.get(area) || 0) : 0;
                const pyS = pySalesMap.get(area) || 0;
                const pyC = this.includeCreditNote ? (pyCNMap.get(area) || 0) : 0;
                thisYearByArea.push({ code: area, text, sum: tyS - tyC });
                pastYearByArea.push({ code: area, text, sum: pyS - pyC });
            }
        } else {
            const arQueryForFilter = (filter: string) => `
                SELECT "AREA" as area,
                    SUM(CASE WHEN "DOCTYPE" = 'IV' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as ivAmt,
                    SUM(CASE WHEN "DOCTYPE" = 'DN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as dnAmt,
                    SUM(CASE WHEN "DOCTYPE" = 'CN' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as cnAmt
                FROM ${arTable}
                WHERE ${filter} GROUP BY "AREA"
            `;
            const [tyAR, pyAR] = await Promise.all([
                queryHead.rawQuery(arQueryForFilter(tyDateFilter)),
                queryHead.rawQuery(arQueryForFilter(pyDateFilter)),
            ]);

            const processAR = (rows: any[], target: typeof thisYearByArea) => {
                for (const r of rows) {
                    const area = String(r.area);
                    if (!area) { continue; }
                    const iv = Number(r.ivAmt) || 0;
                    const dn = this.includeDebitNote ? (Number(r.dnAmt) || 0) : 0;
                    const cn = this.includeCreditNote ? (Number(r.cnAmt) || 0) : 0;
                    target.push({ code: area, text: areaMap.get(area) || area, sum: iv + dn - cn });
                }
            };
            processAR(tyAR, thisYearByArea);
            processAR(pyAR, pastYearByArea);
        }
        this.generateLoadingText(0.95);

        thisYearByArea.sort((a, b) => b.sum - a.sum);

        const pastYearMap = new Map<string, number>();
        for (const p of pastYearByArea) { pastYearMap.set(p.code, p.sum); }

        this.xAxisData = thisYearByArea.map((dd) => dd.text);

        this.series = [
            {
                name: 'This Year', type: 'bar', smooth: true,
                barWidth: '40%', color: GobiColor.COLOR1,
                data: thisYearByArea.map((dd) => dd.sum),
            },
            {
                name: 'Last Year', type: 'bar', smooth: true,
                barWidth: '40%', color: GobiColor.COLOR3,
                data: thisYearByArea.map((dd) => pastYearMap.get(dd.code) || 0),
            },
        ];

        this.saveHistory('series', 'xAxisData');
    }
}
